import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { AuthProvider, profileComplete, useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { FullSpinner } from "@/components/Common";
import Layout from "@/components/Layout";
import ClientLogin, { ClientAccept, ClientHome, ClientNoWorkspace } from "@/pages/ClientLogin";
import ClientPortalHome from "@/pages/ClientPortalHome";
import ClientOutreach from "@/pages/ClientOutreach";
import ClientWelcome from "@/pages/ClientWelcome";
import DemoVideoPage from "@/components/demo/ProductDemo";
import DemoClip from "@/components/demo/DemoClip";
import { clientAllowed, clientHome } from "@/lib/clients";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import Onboarding from "@/pages/Onboarding";
import Search from "@/pages/Search";
import Saved from "@/pages/Saved";
import Tracker from "@/pages/Tracker";
import AIStudio from "@/pages/AIStudio";
import Chat from "@/pages/Chat";
import AutoApply from "@/pages/AutoApply";
import Alerts from "@/pages/Alerts";
import Wellbeing from "@/pages/Wellbeing";
import WisdomRoom from "@/pages/wb/WisdomRoom";
import Companion from "@/pages/wb/Companion";
import AdminWellbeing from "@/pages/wb/AdminWellbeing";
import { Boost, Growth, Progress, Recommendations, Reflection, Value } from "@/pages/wb/Pages";
import { Journal, WbSettings } from "@/pages/wb/Journal";
import { Journey, Journeys } from "@/pages/wb/Journeys";
import Upgrade from "@/pages/Upgrade";
import Referrals from "@/pages/Referrals";
import Admin from "@/pages/Admin";
import Calendar from "@/pages/Calendar";
import AdminScans from "@/pages/AdminScans";
import CCOverview from "@/pages/admin/CCOverview";
import Advisor from "@/pages/admin/Advisor";
import Profitability from "@/pages/admin/Profitability";
import GrowthFunnel from "@/pages/admin/GrowthFunnel";
import FreeResumeCheck from "@/pages/FreeResumeCheck";
import { TrialWelcome } from "@/components/TrialWelcome";
import { TourHost } from "@/components/tour/TourHost";
import Prospects from "@/pages/admin/Prospects";
import ProspectProfile from "@/pages/admin/ProspectProfile";
import EmailMarketing from "@/pages/admin/EmailMarketing";
import BlindProfile from "@/pages/BlindProfile";
import { AICampaigns, Health, ResumeIntel, Revenue } from "@/pages/admin/CCPages";
import EmailVerification from "@/pages/admin/EmailVerification";
import Profile from "@/pages/Profile";
import ResumeStudio from "@/pages/ResumeStudio";
import Insights from "@/pages/Insights";
import PublicProfile from "@/pages/PublicProfile";
import PrivacyPromise from "@/pages/PrivacyPromise";
import ChatLeads from "@/pages/admin/ChatLeads";
import PeopleFinder from "@/pages/PeopleFinder";
import AllContacts from "@/pages/AllContacts";
import Recruiter from "@/pages/Recruiter";
import RecruiterDesk from "@/pages/admin/RecruiterDesk";
import SIDashboard from "@/pages/admin/sourcing/SIDashboard";
import TeamAccess from "@/pages/admin/TeamAccess";
import ClientAdmin from "@/pages/admin/ClientAdmin";
import ClientBilling from "@/pages/ClientBilling";
import SINewProject from "@/pages/admin/sourcing/SINewProject";
import SIProject from "@/pages/admin/sourcing/SIProject";
import SISettings from "@/pages/admin/sourcing/SISettings";
import SIQuick from "@/pages/admin/sourcing/SIQuick";
import SITalent from "@/pages/admin/sourcing/SITalent";
import RevenueCenter from "@/pages/admin/RevenueCenter";
import MailboxSetupPage from "@/pages/admin/MailboxSetupPage";
import SalesInboxPage from "@/pages/admin/SalesInboxPage";
import { RootRoute, isProPage } from "@/lib/clientHost";
import { SiteChat } from "@/components/chat/SiteChat";
import { AnalyticsTracker } from "@/components/analytics/AnalyticsTracker";
import VisitorIntel from "@/pages/admin/VisitorIntel";

const AuthCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { loginWithSession } = useAuth();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(location.hash.slice(1)).get("session_id");
    loginWithSession(sid)
      .then(() => { const back = sessionStorage.getItem("rpopro_return"); navigate(back?.startsWith("/") ? back : "/search", { replace: true }); })
      .catch((e) => {
        toast.error(errMsg(e));
        navigate("/login", { replace: true });
      });
  }, [location.hash, loginWithSession, navigate]);
  return <FullSpinner label="Signing you in" />;
};

const RequireAuth = ({ children, onboarding }) => {
  const { status, profile, client, isAdmin } = useAuth();
  const loc = useLocation();
  if (status === "loading") return <FullSpinner />;
  if (status === "anon") {
    if (loc.pathname === "/upgrade" && loc.search) sessionStorage.setItem("rpopro_return", loc.pathname + loc.search);
    return <Navigate to={loc.pathname.startsWith("/client") || localStorage.getItem("rpopro_portal") === "client" ? "/client/login" : "/login"} replace />;
  }
  if (client) localStorage.setItem("rpopro_portal", "client");
  if (client) return clientAllowed(client, loc.pathname) ? children : <Navigate to={clientHome(client)} replace />;
  if (localStorage.getItem("rpopro_portal") === "client") return <ClientNoWorkspace />;
  if (!onboarding && !profileComplete(profile) && !(isAdmin && loc.pathname.startsWith("/admin"))) {
    if (loc.pathname === "/upgrade" && loc.search) sessionStorage.setItem("rpopro_return", loc.pathname + loc.search);
    return <Navigate to="/onboarding" replace />;
  }
  return children;
};

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
    {!location.pathname.startsWith("/demo-") && !isProPage(location.pathname) && <><TrialWelcome />
    <TourHost />
    <SiteChat /></>}
    <AnalyticsTracker />
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/client/login" element={<ClientLogin />} />
      <Route path="/client/accept" element={<ClientAccept />} />
      <Route path="/client/welcome" element={<ClientWelcome />} />
      <Route path="/demo-video" element={<DemoVideoPage />} />
      <Route path="/demo-clip" element={<DemoClip />} />
      <Route path="/client" element={<RequireAuth><ClientHome /></RequireAuth>} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/u/:slug" element={<PublicProfile />} />
      <Route path="/c/:token" element={<BlindProfile />} />
      <Route path="/privacy-promise" element={<PrivacyPromise />} />
      <Route path="/free-resume-check" element={<FreeResumeCheck />} />
      <Route path="/onboarding" element={<RequireAuth onboarding><Onboarding /></RequireAuth>} />
      <Route element={<RequireAuth><Layout /></RequireAuth>}>
        <Route path="/search" element={<Search />} />
        <Route path="/saved" element={<Saved />} />
        <Route path="/tracker" element={<Tracker />} />
        <Route path="/ai-studio" element={<AIStudio />} />
        <Route path="/ai-studio/chat/:modeId" element={<Chat />} />
        <Route path="/auto-apply" element={<AutoApply />} />
        <Route path="/alerts" element={<Alerts />} />
        <Route path="/wellbeing" element={<Wellbeing />} />
        <Route path="/wellbeing/wisdom" element={<WisdomRoom />} />
        <Route path="/wellbeing/reflection" element={<Reflection />} />
        <Route path="/wellbeing/recommendations" element={<Recommendations />} />
        <Route path="/wellbeing/progress" element={<Progress />} />
        <Route path="/wellbeing/value" element={<Value />} />
        <Route path="/wellbeing/growth" element={<Growth />} />
        <Route path="/wellbeing/boost" element={<Boost />} />
        <Route path="/wellbeing/journal" element={<Journal />} />
        <Route path="/wellbeing/settings" element={<WbSettings />} />
        <Route path="/wellbeing/companion" element={<Companion />} />
        <Route path="/wellbeing/journeys" element={<Journeys />} />
        <Route path="/wellbeing/journey/:id" element={<Journey />} />
        <Route path="/admin/wellbeing" element={<AdminWellbeing />} />
        <Route path="/insights" element={<Insights />} />
        <Route path="/calendar" element={<Calendar />} />
        <Route path="/upgrade" element={<Upgrade />} />
        <Route path="/referrals" element={<Referrals />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/resume-studio" element={<ResumeStudio />} />
        <Route path="/people-finder" element={<PeopleFinder />} />
        <Route path="/people-finder/contacts" element={<AllContacts />} />
        <Route path="/recruiter" element={<Recruiter />} />
        <Route path="/admin/recruiters" element={<RecruiterDesk />} />
        <Route path="/admin/vi" element={<VisitorIntel />} />
        <Route path="/admin/vi/:section" element={<VisitorIntel />} />
        <Route path="/admin/si" element={<SIDashboard />} />
        <Route path="/admin/access" element={<TeamAccess />} />
        <Route path="/admin/mailboxes" element={<MailboxSetupPage />} />
        <Route path="/admin/sales-inbox" element={<SalesInboxPage />} />
        <Route path="/admin/clients" element={<ClientAdmin />} />
        <Route path="/client/billing" element={<ClientBilling />} />
        <Route path="/client/home" element={<ClientPortalHome />} />
        <Route path="/client/outreach" element={<ClientOutreach />} />
        <Route path="/admin/si/new" element={<SINewProject />} />
        <Route path="/admin/si/settings" element={<SISettings />} />
        <Route path="/admin/si/quick" element={<SIQuick />} />
        <Route path="/admin/si/talent" element={<SITalent />} />
        <Route path="/admin/si/p/:id" element={<SIProject />} />
        <Route path="/admin/si/p/:id/:tab" element={<SIProject />} />
        <Route path="/admin/recruiters/:tab" element={<RecruiterDesk />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/admin/scans" element={<AdminScans />} />
        <Route path="/admin/chat-leads" element={<ChatLeads />} />
        <Route path="/admin/cc" element={<CCOverview />} />
        <Route path="/admin/cc/advisor" element={<Advisor />} />
        <Route path="/admin/cc/funnel" element={<GrowthFunnel />} />
        <Route path="/admin/cc/profit" element={<Profitability />} />
        <Route path="/admin/cc/prospects" element={<Prospects />} />
        <Route path="/admin/rcc" element={<RevenueCenter />} />
        <Route path="/admin/rcc/radar/*" element={<RevenueCenter radar />} />
        <Route path="/admin/rcc/:section" element={<RevenueCenter />} />
        <Route path="/admin/cc/email-verification" element={<EmailVerification />} />
        <Route path="/admin/cc/prospects/:key" element={<ProspectProfile />} />
        <Route path="/admin/cc/resume" element={<ResumeIntel />} />
        <Route path="/admin/cc/revenue" element={<Revenue />} />
        <Route path="/admin/cc/health" element={<Health />} />
        <Route path="/admin/cc/ai" element={<AICampaigns />} />
        <Route path="/admin/mk" element={<EmailMarketing />} />
      </Route>
      <Route path="/" element={<RootRoute />} />
      <Route path="*" element={<Navigate to="/search" replace />} />
    </Routes>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster theme="dark" position="top-right" richColors closeButton />
      </AuthProvider>
    </BrowserRouter>
  );
}
